import React, { useMemo, useState, useEffect } from "react";
import { useToast } from "@chakra-ui/react";

import { toast } from "sonner";
import axios from "./axiosConfig";
import API_BASE_URL from "config/api";
import AutoLinkIngredientModal from "./AutoLinkIngredientModal";
import { useInterval } from "contexts/IntervalContext";
import InventoryAccordion from "./InventoryAccordion";
export default function InventoryTable() {
  const { interval } = useInterval();
  const costHeader = useMemo(() => {
    if (interval === "daily") return "Daily Cost";
    if (interval === "monthly") return "Monthly Cost";
    return "Weekly Cost";
  }, [interval]);
  const columns = useMemo(
  () => [
    { Header: "Name", accessor: "name" },
    { Header: "Current Stock", accessor: "estimated_remaining" },
    { Header: "Days of Cover", accessor: "days_of_cover" },
    // { Header: "Stock Level", accessor: "stock_level" },
    { Header: costHeader, accessor: "forecast_cost" }
  ],
  [costHeader]
);

  const [data, setData] = useState([]);
  const [filter, setFilter] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [newRow, setNewRow] = useState({ name: "", current_quantity: "", unit: "", category: "" });
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [isSyncNeeded, setIsSyncNeeded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showAutoLinkModal, setShowAutoLinkModal] = useState(false);
  const [gptIngredients, setGptIngredients] = useState([]);
  const [products, setProducts] = useState([]);
  const [productList, setProductList] = useState([]);
  const [openRow, setOpenRow] = useState(null);
  const currencySymbolMap = {
    GBP: "£",
    USD: "$",
    KRW: "₩",
    EUR: "€",
  };

      const fetchInventory = async () => {
        try {
          const token = localStorage.getItem("access_token");

          const res = await axios.get(
            `${API_BASE_URL}/api/inventory/inventory?period=${interval}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );

          setData(res.data);

          const syncRes = await axios.get(
            `${API_BASE_URL}/api/inventory/check/newinventory`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setIsSyncNeeded(syncRes.data.has_new_inventory);

        } catch (err) {
          console.error("Error fetching inventory:", err);
        }
      };

    useEffect(() => {
      fetchInventory();
    }, [interval]);



     useEffect(() => {
      const fetchProducts = async () => {
        try {
          const token = localStorage.getItem("access_token");
          const res = await axios.get(`${API_BASE_URL}/api/products/`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setProductList(res.data);
        } catch (err) {
          console.error("Failed to fetch products:", err);
        }
      };

      fetchProducts();
    }, []);

  const handleSave = async () => {
    if (!newRow.name || !newRow.current_quantity) return alert("Please fill all fields");
    try {
      const token = localStorage.getItem("access_token");
      const res = await axios.post(`${API_BASE_URL}/api/inventory/inventory/`, newRow, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData([...data, res.data]);
      setIsCreating(false);
      setNewRow({ name: "", current_quantity: "", unit: "", category: "" });
    } catch (err) {
      console.error("Error saving data:", err);
    }
  };

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem("access_token");
      await Promise.all(
        selectedItems.map((id) =>
          axios.delete(`${API_BASE_URL}/api/inventory/inventory/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          })
        )
      );
      setData(data.filter((item) => !selectedItems.includes(item.id)));
      setSelectedItems([]);
      setIsDeleting(false);
    } catch (err) {
      console.error("Error deleting:", err);
    }
  };

  const handleAutoSync = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem("access_token");

      // GPT API 호출 (예: auto-sync → auto-link-gpt 로 교체)
      const res = await axios.post(
        `${API_BASE_URL}/api/inventory/auto-sync/`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // GPT가 추천한 재료 데이터를 popup으로 넘김
      const normalized = (res.data.ingredients || []).map(item => ({
        id: crypto.randomUUID(),
        name: item.name,
        category: item.category ?? "ingredient",  // 백엔드에서 없으므로 기본값 지정
        recommended_unit: item.recommended_unit ?? null,
        avg_consumption: item.avg_consumption ?? null,
        probability: item.probability ?? 0,
        finished_product: item.finished_product ?? false,
        selected: item.selected ?? true,
        remaining: 50, // 기본 50%
        product_usage_hint: item.product_usage_hint || {},
        
        purchase_quantity: item.purchase_quantity ?? null,
        purchase_unit: item.purchase_unit ?? item.recommended_unit ?? null,
        purchase_price: item.purchase_price ?? null,

        // 🔥 실제로 사용하는 product_usage는 아직 비워둠
        product_usage: undefined,
            }));

      setGptIngredients(normalized);
      console.log("normalized data",normalized)
      console.log("res data", res.data)
      // 팝업 열기
      setShowAutoLinkModal(true);
      
    } catch (err) {
     
      alert("Auto-link failed.");
    } finally {
      setIsLoading(false);
    }
  };
  // const toast = useToast();
  const handleSaveIngredients = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const payload = { ingredients: gptIngredients.filter(i => i.selected) };

      console.log("🔥 SEND TO DJANGO:", JSON.stringify(payload, null, 2));

      await axios.post(
        `${API_BASE_URL}/api/inventory/save-gpt-ingredients/`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // alert("Ingredients saved!");
      await fetchInventory();
      setShowAutoLinkModal(false);
      console.log("BEFORE TOAST");
      toast.success("Ingredients saved successfully");


    } catch (err) {
      console.error("Save ingredients error:", err);
      alert("Failed to save ingredients.");
    }
  };

  const handleUpdateInventory = async (id, updatedData) => {
    const token = localStorage.getItem("access_token");

    try {
      // ✅ 1) Quick adjust / Reorder
      if (updatedData?.action === "adjust_stock") {
        await axios.patch(
          `${API_BASE_URL}/api/inventory/inventory/${id}/adjust-stock/?period=${interval}`,
          {
            mode: updatedData.mode,   // "add" | "set"
            unit: updatedData.unit,   // "pack" | "unit"
            value: updatedData.value, // number
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        // ✅ 핵심: projection 포함된 list 다시 가져오기
        await fetchInventory();
        return;
      }

      // ✅ 2) 일반 필드 수정 (legacy)
      const res = await axios.patch(
        `${API_BASE_URL}/api/inventory/inventory/${id}/`,
        updatedData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // legacy는 res.data 그대로 써도 OK
      setData((prev) =>
        prev.map((item) => (item.id === id ? res.data : item))
      );

      return res.data;
    } catch (err) {
      console.error("Update failed", err);
      throw err;
    }
  };
  
  return (
    <>
    <div className="bg-[#121212] p-6 rounded-2xl border border-neutral-800 shadow-lg">
      <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-2">
        <h2 className="text-xl font-semibold">Inventory Table</h2>
        <div className="flex flex-wrap gap-2">
          <input
            type="text"
            placeholder="Search..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-3 py-1.5 bg-neutral-900 border border-neutral-700 rounded-md text-sm"
          />
          <button
            onClick={handleAutoSync}
            // disabled={!isSyncNeeded || isLoading}
            disabled={isLoading}
            // className={`px-4 py-1.5 rounded-md text-sm font-medium ${
            //   isSyncNeeded ? "bg-yellow-500 hover:bg-yellow-400" : "bg-gray-700 cursor-not-allowed"
            // }`}
            className={`px-4 py-1.5 rounded-md text-sm font-medium ${
              isSyncNeeded ? "bg-yellow-500 hover:bg-yellow-400" : "bg-gray-700 hover:bg-gray-600"
            }`}
          >
            {isLoading ? "Syncing..." : "Auto link Product"}
          </button>
          <button onClick={() => setIsCreating(true)} className="bg-blue-500 hover:bg-blue-400 text-white px-4 py-1.5 rounded-md text-sm">
            Create
          </button>
          <button
            onClick={() => setIsDeleting(!isDeleting)}
            className="bg-red-500 hover:bg-red-400 text-white px-4 py-1.5 rounded-md text-sm"
          >
            {isDeleting ? "Cancel" : "Delete"}
          </button>
          {isDeleting && (
            <button onClick={handleDelete} className="bg-red-700 hover:bg-red-600 text-white px-4 py-1.5 rounded-md text-sm">
              Confirm
            </button>
          )}
        </div>
      </div>

      <p className={`text-sm mb-3 ${isSyncNeeded ? "text-green-400" : "text-red-400"}`}>
        {isSyncNeeded
          ? "Product and Inventory are linked."
          : "Product and Inventory have not been linked yet."}
      </p>

      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-neutral-900 text-gray-300">
            {isDeleting && <th className="py-3 px-4 text-left">Select</th>}
            {columns.map((col, i) => (
              <th key={i} className="py-3 px-4 text-left border-b border-neutral-800">
                {col.Header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {isCreating && (
            <tr className="bg-neutral-800">
              <td className="py-2 px-4">
                <input
                  placeholder="Name"
                  value={newRow.name}
                  onChange={(e) => setNewRow({ ...newRow, name: e.target.value })}
                  className="bg-neutral-900 text-white px-2 py-1 rounded-md w-full"
                />
              </td>
              <td className="py-2 px-4">
                <input
                  placeholder="Category"
                  value={newRow.category}
                  onChange={(e) => setNewRow({ ...newRow, category: e.target.value })}
                  className="bg-neutral-900 text-white px-2 py-1 rounded-md w-full"
                />
              </td>
              <td className="py-2 px-4">
                <input
                  placeholder="Quantity"
                  value={newRow.current_quantity}
                  onChange={(e) => setNewRow({ ...newRow, current_quantity: e.target.value })}
                  className="bg-neutral-900 text-white px-2 py-1 rounded-md w-full"
                />
              </td>
              <td colSpan="2" className="py-2 px-4">
                <button onClick={handleSave} className="bg-green-600 hover:bg-green-500 text-white px-3 py-1.5 rounded-md mr-2">
                  Save
                </button>
                <button onClick={() => setIsCreating(false)} className="bg-gray-700 hover:bg-gray-600 px-3 py-1.5 rounded-md text-white">
                  Cancel
                </button>
              </td>
            </tr>
          )}

          {data
            .filter((item) =>
              Object.values(item).some((val) => val?.toString().toLowerCase().includes(filter.toLowerCase()))
            )
            .map((row, i) => (
              <React.Fragment key={i}>

               {/* <tr key={i} className="hover:bg-neutral-800 border-b border-neutral-900"> */}
              <tr
                  key={i}
                  className="hover:bg-neutral-800 border-b border-neutral-900 cursor-pointer"
                  onClick={() => setOpenRow(openRow === row.id ? null : row.id)}
                >
                {isDeleting && (
                  <td className="py-2 px-4">
                    <input
                      type="checkbox"
                      checked={selectedItems.includes(row.id)}
                      onChange={() =>
                        setSelectedItems((prev) =>
                          prev.includes(row.id) ? prev.filter((id) => id !== row.id) : [...prev, row.id]
                        )
                      }
                    />
                  </td>
                )}
               
               
                  {columns.map((col, j) => {
                    const value = row[col.accessor];

                    // ✅ Current Stock (estimated_remaining)
                    if (col.accessor === "estimated_remaining") {
                      if (value == null) {
                        return (
                          <td key={j} className="py-2 px-4 text-gray-400">
                            —
                          </td>
                        );
                      }

                      const remaining = Number(value);
                      const unit = row.display_unit || "";

                      // purchase_quantity가 있는 경우
                      if (row.purchase_quantity) {
                        const packSize = Number(row.purchase_quantity);
                        const packCount = packSize > 0
                          ? (remaining / packSize)
                          : null;

                        return (
                          <td key={j} className="py-2 px-4 text-gray-200">
                            <div className="flex flex-col leading-tight">
                              <span className="font-medium">
                                {packCount != null
                                  ? `${packCount.toFixed(2)} packs`
                                  : "—"}
                              </span>
                              <span className="text-xs text-gray-400">
                                ({Math.round(remaining).toLocaleString()} {unit})
                              </span>
                            </div>
                          </td>
                        );
                      }

                      // purchase_quantity 없는 경우 (기존 방식)
                      return (
                        <td key={j} className="py-2 px-4 text-gray-200">
                          {Math.round(remaining).toLocaleString()} {unit}
                        </td>
                      );
                    }
                    if (col.accessor === "days_of_cover") {
                      const days = Number(value);

                      let color = "text-gray-400";
                      let bg = "";

                      if (!isNaN(days)) {
                        if (days <= 3) {
                          color = "text-red-400";
                          bg = "bg-red-500/10";
                        } else if (days <= 7) {
                          color = "text-yellow-400";
                          bg = "bg-yellow-500/10";
                        } else {
                          color = "text-green-400";
                          bg = "bg-green-500/10";
                        }
                      }

                      return (
                        <td key={j} className="py-2 px-4">
                          <span
                            className={`px-2 py-1 rounded-md text-sm font-medium ${color} ${bg}`}
                          >
                            {days || "—"} days
                          </span>
                        </td>
                      );
                    }

                    if (col.accessor === "forecast_cost") {
                      const cost = Number(value);
                      const currencyCode = row.currency;
                      const symbol = currencySymbolMap[currencyCode] ?? "";
                      return (
                        <td key={j} className="py-2 px-4 text-gray-200">
                          {isNaN(cost)
                            ? "—"
                            : `${symbol}${cost.toFixed(2)}`}
                        </td>
                      );
                    }
                    // 기본 렌더링
                    return (
                      <td key={j} className="py-2 px-4 text-gray-200">
                        {value ?? "—"}
                      </td>
                    );
                  })}

              </tr>
              {openRow === row.id && (
        <tr className="bg-neutral-950">
          <td
            colSpan={columns.length + (isDeleting ? 1 : 0)}
            className="p-0"
          >
            <InventoryAccordion
            item={row}
            onSave={handleUpdateInventory}
          />
          </td>
        </tr>
      )}
    </React.Fragment>
            ))}
        </tbody>
      </table>
    </div>
   {/* modal must NOT be inside the styled container */}
    {showAutoLinkModal && (
  <AutoLinkIngredientModal
    isOpen={showAutoLinkModal}
    onClose={() => setShowAutoLinkModal(false)}
    ingredients={gptIngredients}
    setIngredients={setGptIngredients}
    onSave={handleSaveIngredients}
    products={productList} 
    
  />
)}
    </>
  );
}